import React, { forwardRef, useImperativeHandle, useRef, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericSettingsPane, GenericSettingsPaneHandle } from './GenericSettingsPane';
import { PaneHandle, PaneProps, ParameterItem } from '../accountParameter.types';
import { useStoreState } from '../../../../models/hooks';
import { DeactivateSalaryValidationModal } from './DeactivateSalaryValidationModal';

export const RequisitionPane = forwardRef<PaneHandle, PaneProps>(({ onSave, setDirty, showActions }, ref) => {
  const genericPaneRef = useRef<GenericSettingsPaneHandle>(null);
  const account = useStoreState((state) => state.app.accounts);
  const { t } = useTranslation('accountParameter');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [externalResetCounter, setExternalResetCounter] = useState(0);

  // ref so handleRowsChange always reads the latest value without being recreated
  const positionSalaryCeilingValidationRef = useRef(account?.configuration?.enablePositionSalaryCeilingValidation);
  positionSalaryCeilingValidationRef.current = account?.configuration?.enablePositionSalaryCeilingValidation;

  useImperativeHandle(ref, () => ({
    save: () => genericPaneRef.current?.save(),
    reset: () => {},
  }));

  const handleSave = useCallback(
    async (updates: Record<string, any>) => {
      const transformedUpdates = { ...updates };
      if ('enablePositionSalaryCeilingValidation' in transformedUpdates) {
        transformedUpdates.enablePositionSalaryCeilingValidation = !transformedUpdates.enablePositionSalaryCeilingValidation;
      }
      return onSave(transformedUpdates);
    },
    [onSave],
  );

  const handleModalConfirmDeactivate = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setExternalResetCounter((c) => c + 1);
  }, []);

  // Stable callback — reads latest value via ref, never recreated
  const handleRowsChange = useCallback((rows: ParameterItem[]) => {
    const salaryValidationRow = rows.find((r) => r.key === 'enablePositionSalaryCeilingValidation');
    // Show modal when "Disable" toggle is turned ON while feature was previously enabled
    // Treat undefined as true — matches the `?? true` default used in items
    const isCurrentlyEnabled = positionSalaryCeilingValidationRef.current !== false;
    if (isCurrentlyEnabled && salaryValidationRow?.value === true) {
      setIsModalOpen(true);
    }
  }, []); // empty deps — ref keeps it fresh without recreating the function
  const items = useMemo(
    () => [
      {
        key: 'requisitionWebHookurl',
        label: t('requisitionPane.requisitionWebhookURL'),
        description: t('requisitionPane.requisitionWebhookURLDescription'),
        value: account?.requisitionWebHookurl,
        type: 'string' as const,
        validationType: 'url' as const,
        isRequired: true,
      },

      {
        key: 'requisitionActiveThresholdDays',
        label: t('requisitionPane.requisitionActiveThresholdDays'),
        description:
          t('requisitionPane.requisitionActiveThresholdDaysDescription') + t('accountParametersView.positionOverWrittenWarning'),
        value: account?.configuration?.requisitionActiveThresholdDays,
        unit: t('requisitionPane.days'),
        min: 1,
        max: 30,
        type: 'number' as const,
        isRequired: true,
      },
      {
        key: 'enablePositionSalaryCeilingValidation',
        label: t('requisitionPane.positionSalaryCeilingValidation'),
        description: t('requisitionPane.positionSalaryCeilingValidationDescription'),
        value: !(account?.configuration?.enablePositionSalaryCeilingValidation ?? true),
        type: 'boolean' as const,
      },
    ],
    [account, t],
  );

  return (
    <>
      <GenericSettingsPane
        ref={genericPaneRef}
        onSave={handleSave}
        setDirty={setDirty}
        showInlineActions={showActions}
        items={items}
        onRowsChange={handleRowsChange}
        externalResetCounter={externalResetCounter}
      />
      <DeactivateSalaryValidationModal
        isOpen={isModalOpen}
        onConfirm={handleModalConfirmDeactivate}
        onCancel={handleModalCancel}
      />
    </>
  );
});
